import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import useSWR from "swr";
import type { AxiosError } from "axios";
import axios from "axios";
import { Controller, useForm } from "react-hook-form";
import {
  ButtonWithConfirmDialog,
  DeleteButton,
  PrimaryButtonLargeV2,
  TertiaryButtonLargeV2,
  TertiaryButtonMediumV2,
  IconButtonMedium,
} from "../../../../../components/Buttons/Buttons";
import { EditIcon, PlusIcon } from "../../../../../components/Icons/Icons";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import { SearchBar } from "../../../../../components/SearchBar/SearchBar";
import { Table } from "../../../../../components/Table/Table";
import { SlideOut } from "../../../../../components/SlideOut/SlideOut";
import { SelectBoxV2 } from "../../../../../components/SelectBoxV2/SelectBoxV2";
import { Form } from "../../../../../layout/FormLayout";
import { useAuthContext } from "../../../../../components/Auth";
import { endpoints } from "../../../../../endpoints";
import type {
  OptionType,
  ProfileComparisonConfig,
  ProfileComparisonConfigsResponse,
  ProfileComparisonTargetType,
  UUID,
} from "../../../../../types/types";
import type {
  AttributeInput,
  AttributeTemplateSchema,
  CollectionSchema,
} from "../../../../../types/types.PIM";
import { TablePlaceholder, useStoreState } from "../../../../../util/util";
import { strings } from "../../../../../util/strings";
import { Description, SectionTitle } from "./TemplateSettings";

const KEY_ATTRIBUTE_INPUT_TYPES: AttributeInput[] = [
  "single_select",
  "multi_select",
];

const EXCLUDED_COLLECTION_NAMES = new Set(["assets"]);

const SearchRow = styled.div`
  margin: ${({ theme }) => theme.resolve("spacing_lg")} 0
    ${({ theme }) => theme.resolve("spacing_md")};
  max-width: 420px;
`;

const CountLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.small};
  color: ${({ theme }) => theme.secondaryTextColor};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_sm")};
`;

const IconGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
  align-items: center;
  justify-content: flex-end;
`;

const EditButtonContainer = styled.div`
  width: 30px;
  height: 30px;
  display: flex;
  border-radius: 25px;
  border: ${({ theme }) => theme.resolve("border_md")} solid transparent;
  align-items: center;
  justify-content: center;
  padding: 3px;
  path {
    fill: ${({ theme }) => theme.semanticColors.iconWhite};
    stroke: ${({ theme }) => theme.primaryTextColor};
  }
  &:active,
  &:focus,
  &:hover {
    background: ${({ theme }) => theme.primaryButtonHover};
    border: ${({ theme }) => theme.resolve("border_md")} solid
      ${({ theme }) => theme.primaryButtonBorder};
    path {
      stroke: ${({ theme }) => theme.primaryTextColor};
      fill: ${({ theme }) => theme.semanticColors.iconWhite};
    }
  }
`;

const AddButtonWrapper = styled.div`
  margin-top: ${({ theme }) => theme.resolve("spacing_sm")};
  svg {
    width: 16px !important;
    height: 16px !important;
  }
`;

const ActionButtonsWrapper = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
  margin-top: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const TableSection = styled.div`
  margin-bottom: ${({ theme }) => theme.resolve("spacing_xl")};
`;

const TargetName = styled.div`
  color: ${({ theme }) => theme.primaryTextColor};
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  line-height: ${({ theme }) => theme.resolve("line_height_md")};
`;

const TargetType = styled.div`
  color: ${({ theme }) => theme.secondaryTextColor};
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  line-height: ${({ theme }) => theme.resolve("line_height_sm")};
  margin-top: 2px;
`;

const TargetOptionPrimary = styled(TargetName)``;

const TargetOptionSecondary = styled(TargetType)``;

type TargetOptionValue = {
  type: ProfileComparisonTargetType;
  id: string;
};

type TargetOption = OptionType<TargetOptionValue> & {
  typeLabel: string;
};

type FormValues = {
  target: TargetOption | null;
  key_attribute: OptionType<string> | null;
};

function isEligibleCollection(collection: CollectionSchema): boolean {
  return !EXCLUDED_COLLECTION_NAMES.has(
    (collection.name || "").trim().toLowerCase()
  );
}

function encodeTargetValue(
  type: ProfileComparisonTargetType,
  id: string
): TargetOptionValue {
  return { type, id };
}

function formatKeyAttributeInputType(
  inputType: AttributeInput,
  t: (key: string) => string
): string {
  if (inputType === "single_select") return t("Single-Select");
  if (inputType === "multi_select") return t("Multi-Select");
  return inputType.replace(/_/g, "-");
}

function formatKeyAttributeLabel(
  name: string,
  inputType: AttributeInput,
  t: (key: string) => string
): string {
  return `${name} • ${formatKeyAttributeInputType(inputType, t)}`;
}

type ProfileComparisonSettingsProps = {
  templateID: UUID;
  template: AttributeTemplateSchema;
};

export function ProfileComparisonSettings({
  templateID,
  template,
}: ProfileComparisonSettingsProps) {
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();
  const { hasPermission } = useAuthContext();
  const { notifySuccess, notifyError } = useContext(Notifications);
  const canModify = hasPermission("modify_templates");

  const [showSlideOut, setShowSlideOut] = useState(false);
  const [editing, setEditing] = useState<ProfileComparisonConfig | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const configsUrl =
    endpoints.v2_tenants_id_pim_templates_id_settings_profileComparison(
      tenant_id,
      templateID
    );
  const { data, mutate, error } =
    useSWR<ProfileComparisonConfigsResponse>(configsUrl);

  const configs = useMemo(() => data?.data ?? [], [data?.data]);

  const filteredConfigs = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return configs;
    return configs.filter((row) => {
      const name = (
        row.target_display_name ||
        row.target_name ||
        ""
      ).toLowerCase();
      const type = row.target_type.toLowerCase();
      const keyAttr = (
        row.key_attribute_display_name ||
        row.key_attribute_name ||
        ""
      ).toLowerCase();
      return (
        name.includes(query) || type.includes(query) || keyAttr.includes(query)
      );
    });
  }, [configs, searchQuery]);

  const count = filteredConfigs.length;

  const { control, handleSubmit, reset, watch, setValue, errors, formState } =
    useForm<FormValues>({
      defaultValues: { target: null, key_attribute: null },
    });

  const watchedTarget = watch("target");
  const watchedKey = watch("key_attribute");
  const selectedType = watchedTarget?.value?.type;
  const selectedTargetId = watchedTarget?.value?.id;
  const isCollectionSelected = selectedType === "collection";

  const configuredTargetIds = useMemo(
    () => new Set(configs.map((c) => c.target_id)),
    [configs]
  );

  // Flat list: name + Group/Collection subtitle (per design), not section headings.
  const targetOptions: TargetOption[] = useMemo(() => {
    const groups = (template.groups ?? [])
      .filter(
        (g) => editing?.target_id === g.id || !configuredTargetIds.has(g.id)
      )
      .map((g) => ({
        value: encodeTargetValue("group", g.id),
        label: g.display_name || g.name,
        typeLabel: t("Group"),
      }));
    const collections = (template.collections ?? [])
      .filter(isEligibleCollection)
      .filter(
        (c) => editing?.target_id === c.id || !configuredTargetIds.has(c.id)
      )
      .map((c) => ({
        value: encodeTargetValue("collection", c.id),
        label: c.display_name || c.name,
        typeLabel: t("Collection"),
      }));
    return [...groups, ...collections];
  }, [template, configuredTargetIds, editing, t]);

  const keyAttributeOptions: OptionType<string>[] = useMemo(() => {
    if (!selectedTargetId || selectedType !== "collection") return [];
    const collection = (template.collections ?? []).find(
      (c) => c.id === selectedTargetId
    );
    if (!collection) return [];
    return (collection.columns ?? [])
      .filter((col) =>
        KEY_ATTRIBUTE_INPUT_TYPES.includes(col.input_type as AttributeInput)
      )
      .map((col) => ({
        value: col.id,
        label: formatKeyAttributeLabel(
          col.display_name || col.name,
          col.input_type as AttributeInput,
          t
        ),
      }));
  }, [template, selectedTargetId, selectedType, t]);

  const formatTargetOptionLabel = useCallback(
    (option: TargetOption, { context }: { context: "menu" | "value" }) => {
      if (context === "value") {
        return option.label;
      }
      return (
        <div>
          <TargetOptionPrimary>{option.label}</TargetOptionPrimary>
          <TargetOptionSecondary>{option.typeLabel}</TargetOptionSecondary>
        </div>
      );
    },
    []
  );

  const openAdd = () => {
    setEditing(null);
    reset({ target: null, key_attribute: null });
    setShowSlideOut(true);
  };

  const openEdit = (row: ProfileComparisonConfig) => {
    setEditing(row);
    const targetOpt: TargetOption = {
      value: encodeTargetValue(row.target_type, row.target_id),
      label: row.target_display_name || row.target_name,
      typeLabel: row.target_type === "group" ? t("Group") : t("Collection"),
    };
    let keyOpt: OptionType<string> | null = null;
    if (row.key_attribute_id != null) {
      const collection = (template.collections ?? []).find(
        (c) => c.id === row.target_id
      );
      const column = collection?.columns?.find(
        (col) => col.id === row.key_attribute_id
      );
      const name =
        row.key_attribute_display_name ||
        row.key_attribute_name ||
        row.key_attribute_id;
      keyOpt = {
        value: row.key_attribute_id,
        label: column
          ? formatKeyAttributeLabel(
              name,
              column.input_type as AttributeInput,
              t
            )
          : name,
      };
    }
    reset({ target: targetOpt, key_attribute: keyOpt });
    setShowSlideOut(true);
  };

  const closeSlideOut = () => {
    setShowSlideOut(false);
    setEditing(null);
    reset({ target: null, key_attribute: null });
  };

  // Changing Group/Collection clears Key Attribute (spec).
  useEffect(() => {
    if (!showSlideOut || editing) return;
    setValue("key_attribute", null);
  }, [selectedTargetId, selectedType, showSlideOut, editing, setValue]);

  const canSave = Boolean(
    watchedTarget?.value &&
      (selectedType === "group" ||
        (selectedType === "collection" && watchedKey?.value))
  );

  const onSubmit = async (values: FormValues) => {
    if (!values.target?.value) return;
    const { type, id } = values.target.value;
    const body = {
      target_type: type,
      target_id: id,
      key_attribute_id:
        type === "collection" ? values.key_attribute?.value ?? null : null,
    };
    setIsSubmitting(true);
    try {
      if (editing) {
        await axios.patch(
          endpoints.v2_tenants_id_pim_templates_id_settings_profileComparison_id(
            tenant_id,
            templateID,
            editing.id
          ),
          body
        );
        notifySuccess(t("Comparison configuration updated"));
      } else {
        await axios.post(configsUrl, body);
        notifySuccess(t("Comparison configuration added"));
      }
      await mutate();
      closeSlideOut();
    } catch (err) {
      const message = (err as AxiosError<{ message?: string }>)?.response?.data
        ?.message;
      notifyError(
        message ?? t("There was an error saving the comparison configuration"),
        { error: err }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = useCallback(
    async (row: ProfileComparisonConfig) => {
      try {
        await axios.delete(
          endpoints.v2_tenants_id_pim_templates_id_settings_profileComparison_id(
            tenant_id,
            templateID,
            row.id
          )
        );
        notifySuccess(t("Comparison configuration removed"));
        mutate();
      } catch (err) {
        const message = (err as AxiosError<{ message?: string }>)?.response
          ?.data?.message;
        notifyError(
          message ??
            t("There was an error removing the comparison configuration"),
          { error: err }
        );
      }
    },
    [tenant_id, templateID, mutate, notifySuccess, notifyError, t]
  );

  const columns = useMemo(
    () => [
      {
        header: t("Groups / Collection"),
        accessorKey: "target_display_name",
        cell: ({
          row: { original },
        }: {
          row: { original: ProfileComparisonConfig };
        }) => (
          <div>
            <TargetName>
              {original.target_display_name || original.target_name}
            </TargetName>
            <TargetType>
              {original.target_type === "group" ? t("Group") : t("Collection")}
            </TargetType>
          </div>
        ),
      },
      {
        header: t("Key Attribute"),
        accessorKey: "key_attribute_display_name",
        cell: ({
          row: { original },
        }: {
          row: { original: ProfileComparisonConfig };
        }) => (
          <>
            {original.target_type === "group"
              ? "--"
              : original.key_attribute_display_name ||
                original.key_attribute_name ||
                "--"}
          </>
        ),
      },
      {
        header: "",
        accessorKey: " ",
        cell: ({
          row: { original },
        }: {
          row: { original: ProfileComparisonConfig };
        }) => {
          const typeLabel =
            original.target_type === "group" ? t("group") : t("collection");
          const typeTitle =
            original.target_type === "group" ? t("Group") : t("Collection");
          return (
            <IconGroup>
              <ButtonWithConfirmDialog
                testid={`delete-profile-comparison-${original.id}`}
                Button={DeleteButton}
                handleConfirm={() => handleDelete(original)}
                title={t("Remove {{type}}", { type: typeTitle })}
                confirmMessage={t(
                  "Are you sure you want to remove this {{type}}? It will stop appearing on the comparison page and no product data is affected.",
                  { type: typeLabel }
                )}
                confirmBtnLabel={t("Remove")}
                confirmBtnType="destructive"
                useV2Buttons={true}
                disabled={!canModify}
              />
              <IconButtonMedium
                testid={`edit-profile-comparison-${original.id}`}
                onClick={() => openEdit(original)}
                disabled={!canModify}
                style={{ border: "none", background: "transparent" }}
              >
                <EditButtonContainer>
                  <EditIcon height={16} width={16} />
                </EditButtonContainer>
              </IconButtonMedium>
            </IconGroup>
          );
        },
      },
    ],
    // openEdit closes over template/t; columns rebuild when configs/template change via parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, canModify, handleDelete, template]
  );

  const targetFieldDisabled = Boolean(
    editing && editing.target_type === "collection"
  );
  const keyFieldDisabled =
    !isCollectionSelected ||
    (!editing && !selectedTargetId) ||
    (editing?.target_type === "group" && !isCollectionSelected);

  return (
    <>
      <SectionTitle>{t("Configure Comparison")}</SectionTitle>
      <Description>
        {t(
          "Configure which groups and collections appear on the comparison page and the attribute that identifies each collection's rows."
        )}
      </Description>
      <SearchRow>
        <SearchBar
          placeHolder={t("Search")}
          query={searchQuery}
          handleChange={(e: ChangeEvent<HTMLInputElement>) =>
            setSearchQuery(e.target.value)
          }
          handleClearInput={() => setSearchQuery("")}
          maxWidth="100%"
          dataTestId="profile-comparison-search"
        />
      </SearchRow>
      <CountLabel>{t("{{count}} Fields", { count })}</CountLabel>
      <TableSection>
        <Table
          columns={columns}
          data={filteredConfigs}
          isLoading={!data && !error}
          error={error}
          Placeholder={
            <TablePlaceholder
              message={t("No comparison configurations yet.")}
            />
          }
        />
        <AddButtonWrapper>
          <TertiaryButtonMediumV2
            onClick={openAdd}
            disabled={!canModify}
            datatip={
              !canModify
                ? t("You do not have permission to modify templates")
                : undefined
            }
          >
            <PlusIcon />
            {t("Add")}
          </TertiaryButtonMediumV2>
        </AddButtonWrapper>
      </TableSection>

      <SlideOut
        show={showSlideOut}
        closeFlyout={closeSlideOut}
        title={
          editing
            ? t("Edit Comparison Configuration")
            : t("Configure Comparison")
        }
      >
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            as={SelectBoxV2}
            control={control}
            name="target"
            placeholder={t("Group / Collection")}
            options={targetOptions}
            disabled={targetFieldDisabled}
            formatOptionLabel={formatTargetOptionLabel}
            rules={{
              required: true,
              validate: (val: TargetOption | null) =>
                !!val?.value || strings(t).thisIsARequiredField,
            }}
            errors={errors}
            formState={formState}
          />
          <Controller
            as={SelectBoxV2}
            control={control}
            name="key_attribute"
            placeholder={t("Key Attribute")}
            options={keyAttributeOptions}
            disabled={keyFieldDisabled}
            rules={{
              validate: (val: OptionType<string> | null) => {
                if (!isCollectionSelected) return true;
                return !!val?.value || strings(t).thisIsARequiredField;
              },
            }}
            errors={errors}
            formState={formState}
          />
          <ActionButtonsWrapper>
            <TertiaryButtonLargeV2 type="button" onClick={closeSlideOut}>
              {t("Cancel")}
            </TertiaryButtonLargeV2>
            <PrimaryButtonLargeV2
              type="submit"
              disabled={!canSave || isSubmitting || !canModify}
            >
              {t("Save")}
            </PrimaryButtonLargeV2>
          </ActionButtonsWrapper>
        </Form>
      </SlideOut>
    </>
  );
}
